import { useState } from 'react';
import {
  UserDetailsCard,
  UserDetailsRow,
  UserDetailsRowTitle,
  UserDetailsRowValue,
} from '@/bundle/_Opco/shared/styles';
import { OrganizationUserType } from '@/bundle/_Opco/types/types';
import { formatPhone } from '@/helpers/formatHelpers';
import { UserRole } from '@/bundle/_Opco/shared/UserRole/UserRole';
import { Box } from '@/components/Box/Box';
import EditIcon from '@/images/EditIcon.svg?react';
import { Drawer } from '@/components/Drawer/Drawer';
import { EditMobilePhoneForm } from '@/bundle/_Opco/ui/OrganizationDetails/Details/ui/OrganizationUserAccountDetails/EditMobilePhoneForm';
import { ORGANIZATION_USER_ROLE_ICON_MAP } from '../../../const/const';
import { UPDATE_USER_MOBILE_PHONE_NOTIFICATION, UPDATE_USER_ROLE_NOTIFICATION } from '@/const/shared';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { IconButton } from '@/components/IconButton/IconButton';
import { EditUserRoleForm } from './EditUserRoleForm';
import { ORGANIZATION_USER_ROLE_LABEL_MAP } from '@/const/user';
import {
  isActiveOrganizationUser,
  isInvitedOrganizationUser,
  isUnconfirmedOrganizationUser,
} from '@/helpers/organizationHelpers';
import { OrganizationUserAccountDetailsStatusActions } from './OrganizationUserAccountDetailsStatusActions';

type OrganizationUserAccountDetailsType = {
  user: OrganizationUserType;
  onRefetchUser: () => void;
};

export const OrganizationUserAccountDetails = ({ user, onRefetchUser }: OrganizationUserAccountDetailsType) => {
  const [isEditUserRoleOpen, setIsEditUserRoleOpen] = useState(false);
  const [isEditMobilePhoneOpen, setIsEditMobilePhoneOpen] = useState(false);

  const openEditMobilePhone = () => setIsEditMobilePhoneOpen(true);
  const closeEditMobilePhone = () => setIsEditMobilePhoneOpen(false);

  const closeEditUserRole = () => setIsEditUserRoleOpen(false);

  const onSuccessEditMobilePhone = () => {
    onRefetchUser();
    closeEditMobilePhone();

    showSnackbar(UPDATE_USER_MOBILE_PHONE_NOTIFICATION, { maxWidth: '375px' });
  };

  const onSuccessEditRole = () => {
    onRefetchUser();
    closeEditUserRole();

    showSnackbar(UPDATE_USER_ROLE_NOTIFICATION, { maxWidth: '375px' });
  };

  const { email, mobile_phone, status, role } = user;

  const formattedMobilePhone = formatPhone(mobile_phone);
  const formattedRole = ORGANIZATION_USER_ROLE_LABEL_MAP[role];
  const icon = ORGANIZATION_USER_ROLE_ICON_MAP[role];
  const isInvited = isInvitedOrganizationUser(status);
  const isUnconfirmed = isUnconfirmedOrganizationUser(status);
  const isActive = isActiveOrganizationUser(status);
  const isAvailableToModify = isActive || isInvited || isUnconfirmed;

  return (
    <Box flexDirection='row' columnGap='28px'>
      <Box display='flex' width='100%' columnGap='28px' flexShrink='0'>
        <Box width='100%'>
          <UserDetailsCard>
            <UserDetailsRow>
              <UserDetailsRowTitle>Email Address</UserDetailsRowTitle>
              <UserDetailsRowValue>{email}</UserDetailsRowValue>
            </UserDetailsRow>
            <UserDetailsRow>
              <Box flexDirection='row' justifyContent='space-between'>
                <Box>
                  <UserDetailsRowTitle>Mobile Phone Number</UserDetailsRowTitle>
                  <UserDetailsRowValue>{formattedMobilePhone}</UserDetailsRowValue>
                </Box>
                {isInvited && (
                  <IconButton onClick={openEditMobilePhone} marginLeft='12px'>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </UserDetailsRow>
            <UserDetailsRow>
              <Box flexDirection='row' justifyContent='space-between'>
                <Box>
                  <UserDetailsRowTitle>Role</UserDetailsRowTitle>
                  <UserRole role={formattedRole} icon={icon}></UserRole>
                </Box>
                {isAvailableToModify && (
                  <IconButton onClick={() => setIsEditUserRoleOpen(true)} marginLeft='12px'>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </UserDetailsRow>
            <OrganizationUserAccountDetailsStatusActions user={user} onRefetchUser={onRefetchUser} />
          </UserDetailsCard>
        </Box>
      </Box>

      <Drawer
        isOpen={isEditMobilePhoneOpen}
        header='Edit Mobile Phone'
        subHeader='Please edit mobile phone.'
        onClose={closeEditMobilePhone}
      >
        <EditMobilePhoneForm
          onClose={closeEditMobilePhone}
          mobilePhone={mobile_phone}
          onSuccess={onSuccessEditMobilePhone}
        />
      </Drawer>

      <Drawer
        isOpen={isEditUserRoleOpen}
        header='Edit Role'
        subHeader='Please edit user role.'
        onClose={closeEditUserRole}
      >
        <EditUserRoleForm onClose={closeEditUserRole} role={role} onSuccess={onSuccessEditRole} />
      </Drawer>
    </Box>
  );
};
